import { QuadtreeItem } from 'quadtree-lib';

import Game from '../game';
import BaseObject, { BaseObjectParams } from '../baseObject';
import Scene from '../scene/scene';
import LevelScene from '../scene/LevelScene';
import Behaviour from '../behaviour';
import Sprite, { SpriteParams } from '../sprite/sprite';

import { Iterator } from '../../utils/types';
import { getDistanceBy2XY, getXByAngle, getYByAngle } from '../../utils/geometry';
import { Faction, getFactionTargetGroups } from '../../utils/faction';
import { isBetween } from '../../utils/math';
import drawLifeBar from '../../utils/lifeBar';
import { RGBA } from '../../utils/color';

export interface BuildingAttributes {
  cost?: number;
  constructionTime: number;
  constructionTimeDone: number;
  built?: boolean;
}

export interface ElementParams extends BaseObjectParams {
  scene?: Scene;
  context?: CanvasRenderingContext2D;
  faction?: Faction;
  selectable?: boolean;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  lookAngle?: number;
  lookSpeed?: number;
  moveSpeed?: number;
  moveAngle?: number;
  behaviour?: Behaviour;
  isInWarFog?: boolean;
  spriteParams?: SpriteParams;
  sprite?: Sprite;
  targetsGroups?: Array<Faction>;
  building?: BuildingAttributes;
}

export default abstract class Element extends BaseObject implements ElementParams {
  static numberOfFramesToSkipBetweenEachCollision = 2; // collision every 3 frames

  scene: Scene | LevelScene = Game.instance.scene;
  context: CanvasRenderingContext2D;

  faction: Faction = Faction.PLAYER;
  selectable? = false;
  x = 0;
  y = 0;
  width = 10;
  height = 10;
  lookAngle = 90;
  lookSpeed = 360;
  moveSpeed = 0;
  moveAngle = 90;
  behaviour!: Behaviour;
  isInWarFog?: boolean;
  spriteParams?: SpriteParams;
  sprite!: Sprite;
  targetsGroups?: Array<Faction>;
  building!: BuildingAttributes;
  onClick: () => void = () => { };

  hasCollision = true;
  framesSinceLastCollision = 0;
  externalVector = {
    angle: 0,
    speed: 0,
    duration: 0, // unit: milliseconds
    deceleration: 1000,
    minSpeed: 0,
  };
  blinkCanvas!: HTMLCanvasElement;
  blinkCtx!: CanvasRenderingContext2D;

  abstract update(modifier: number): Element;

  constructor(params: ElementParams) {
    super();
    this.applyParams(params);
    this.context = Game.instance.contexts.mainCanvas;
    this.setSprite();
  }

  build(modifier: number): void {
    if (!this.building.constructionTimeDone) this.building.constructionTimeDone = 0;
    this.building.constructionTimeDone += (modifier * 1000);
    this.building.built = (this.building.constructionTimeDone >= (this.building?.constructionTime ?? 0));
  }

  get isInScene(): boolean {
    return !!this.scene.elements[this.id];
  }

  get isInSceneBoundaries(): boolean {
    const { width, height } = this.scene;
    const x = 0, y = 0;
    return this.scene.quadtree
      .filter(el => el.id === this.id)
      ?.colliding({ x, y, width, height })
      ?.length === 1
  }

  get isInCamera(): boolean {
    if (!('camera' in this.scene)) return true;

    const { camera } = this.scene as LevelScene;
    if (!camera)
      return true;

    if (!this.hasCollision) // no quadtree to use
    {
      return (
        isBetween(
          this.x,
          camera.x - camera.width / 2 - this.width / 2,
          camera.x + camera.width / 2 + this.width / 2
        )
        && isBetween(
          this.y,
          camera.y - camera.height / 2 - this.height / 2,
          camera.y + camera.height / 2 + this.height / 2
        )
      );
    }
    else // default quadtree-based collision algorithm
    {
      return this.scene.quadtree
        ?.filter(el => el.id == this.id)
        ?.colliding({
          x: camera.x - camera.width / 2,
          y: camera.y - camera.height / 2,
          width: camera.width,
          height: camera.height
        })
        ?.length === 1
    }
  }

  get rtsControls() {
    return Game.instance.controlSchemes?.rts;
  }

  get manualControls() {
    return Game.instance.controlSchemes?.manual;
  }

  getScene(): Scene {
    return this.scene;
  }

  getFaction(): Faction {
    return this.faction;
  }

  setSprite(): void {
    if (this.spriteParams) {
      this.sprite = new Sprite(this.spriteParams as SpriteParams);
      this.sprite.element = this;
    }
  }

  create() {
    this.id = this.id ?? BaseObject.getNewIdentifier();
    const { id, x, y, width, height } = this;
    this.scene.elements[id] = this;
    if (this.hasCollision) {
      this.scene.quadtree.push({
        x: x - width / 2,
        y: y - height / 2,
        width,
        height,
        id,
      });
    }
    this.isInWarFog = this.faction !== Faction.PLAYER
      && 'warFog' in this.scene
      && this.scene.warFog !== undefined;

    if (!this.targetsGroups)
      this.targetsGroups = getFactionTargetGroups(this.faction);
    return this;
  }

  remove(): Iterator<Element> {
    delete this.scene.elements[this.id]; // from elements
    const quadTreeItem = this.getQuadTreeItem();
    if (this.hasCollision && quadTreeItem)
      this.scene.quadtree.remove(quadTreeItem); // from quadtree
    return this.scene.elements;
  }

  updateQuadtree(): void {
    /* Update quadtree related object */
    if (!this?.scene?.quadtree)
      return;
    const quadTreeItem = this.getQuadTreeItem();
    if (!quadTreeItem)
      return;
    quadTreeItem.x = this.x - this.width / 2;
    quadTreeItem.y = this.y - this.height / 2;
  }

  getQuadTreeItem(): QuadtreeItem | undefined {
    return this.scene.quadtree
      .find((item: QuadtreeItem) => item.id === this.id)
      .pop();
  }

  draw(): CanvasRenderingContext2D {
    const { context, behaviour, sprite, width, height } = this;

    if (behaviour && this.isSelected())
      behaviour.draw();

    if (!this.isInCamera)
      return context;

    context.save();
    this.prepareContext();

    if (sprite)
      sprite.draw();
    else {
      context.strokeStyle = 'red';
      context.lineWidth = 1;
      context.strokeRect(-width / 2, -height / 2, width, height);
    }
    context.restore();

    this.subDraw(); // additional draw actions

    if (this.isSelected() && !this.isInWarFog)
      this.rtsControls.drawElementSelection(this);
    if (this.isHovered() && !this.isInWarFog)
      this.rtsControls.drawElementHover(this);


    return context;
  }

  subDraw(): CanvasRenderingContext2D {
    return this.context;
  }

  /* Returns a canvas containing a frame of this ship blinking with the desired color */
  buildBlinkFrame(color: RGBA): HTMLCanvasElement {
    if (!this.blinkCtx) {
      this.blinkCanvas = document.createElement('canvas');
      this.blinkCanvas.width = this.sprite.width;
      this.blinkCanvas.height = this.sprite.height;
      const blinkCtx = this.blinkCanvas.getContext('2d');
      if (blinkCtx) {
        this.blinkCtx = blinkCtx;
      }
    }

    this.blinkCtx.globalCompositeOperation = 'source-over';
    this.blinkCtx.fillStyle = RGBA.toString(color);
    this.blinkCtx.fillRect(0, 0, this.blinkCtx.canvas.width, this.blinkCtx.canvas.height);
    this.blinkCtx.globalCompositeOperation = 'destination-in';
    const image = new Image();
    image.src = this.sprite.src;
    this.blinkCtx.drawImage(
      image,
      0,
      0,
      this.blinkCtx.canvas.width,
      this.blinkCtx.canvas.height
    );

    return this.blinkCtx.canvas;
  }

  undraw(margin = 10): CanvasRenderingContext2D {
    this.context.clearRect(
      this.x - margin,
      this.y - margin,
      this.width + margin * 2,
      this.height + margin * 2
    );

    if (this.rtsControls)
      this.rtsControls.undrawElementSelection(this);
    return this.context;
  }

  drawConstructionBar(): void {
    const startColor = { R: 255, G: 255, B: 0, A: 1 } as RGBA; // yellow
    const width = Math.max(this.width, 10);
    const height = Math.max(width / 10, 2);
    const ratio = this.building.constructionTimeDone / this.building.constructionTime;
    drawLifeBar({
      x: this.x - this.width / 2,
      y: this.y + this.height / 2,
      width,
      height,
      ratio,
      context: this.context,
      startColor,
      endColor: startColor
    });
  }

  isSelected(): boolean {
    if (!this.rtsControls?.hoveredElementsIds)
      return false;

    return this.rtsControls.selectedElementsIds.findIndex(id => id === this.id) !== -1;
  }

  isHovered(): boolean {
    if (!this.rtsControls?.hoveredElementsIds)
      return false;

    return this.rtsControls.hoveredElementsIds.findIndex(id => id === this.id) !== -1;
  }

  isManuallyControlled(): boolean {
    return this.manualControls.enabled && this.isSelected();
  }

  setBehaviour(behaviour: Behaviour): void {
    this.behaviour = behaviour;
  }

  translateContext(): void {
    this.context.translate(this.x, this.y);
  }

  rotateContext(): void {
    this.context.rotate(Math.PI / 180 * -(this.lookAngle - 90));
  }

  prepareContext(): void {
    this.translateContext();
    this.rotateContext();
  }

  moveByDistanceAndAngle(distance: number, angle: number): void {
    if (distance === 0)
      return;

    this.x += distance * getXByAngle(angle);
    this.y += distance * getYByAngle(angle);
  }

  move(modifier: number): void {
    return this.moveByDistanceAndAngle(this.moveSpeed * modifier, this.moveAngle);
  }

  moveByExternalVector(modifier: number): void {
    const { angle, speed, duration, deceleration, minSpeed } = this.externalVector;
    if (duration <= 0)
      return;

    this.moveByDistanceAndAngle(speed * modifier, angle);
    this.externalVector.speed = Math.max(speed - deceleration * modifier, minSpeed);
    this.externalVector.duration -= modifier * 1000; // seconds to ms
  }

  stop(): void {
    this.moveSpeed = 0;
  }

  rotateTowardsAngle(angleToReach: number, modifier: number): number {
    if (!modifier || angleToReach === undefined)
      return this.lookAngle;

    /* To prevent angle reversion at 0 */
    let d = angleToReach - (this.lookAngle % 360);
    if (d < (-180))
      d += 360;
    else if (d > 180)
      d -= 360;

    if (d < -this.lookSpeed * modifier) {
      this.lookAngle -= this.lookSpeed * modifier;
      if (this.lookAngle < 0)
        this.lookAngle += 360;
    }
    else if (d > this.lookSpeed * modifier) {
      this.lookAngle += this.lookSpeed * modifier;
      if (this.lookAngle > 360)
        this.lookAngle -= 360;
    }
    else
      this.lookAngle = angleToReach;
    return this.lookAngle;
  }

  getFactionTargetGroups(): Array<Faction> {
    return this.targetsGroups ?? [];
  }

  getSelectableQuadtree(): Quadtree<QuadtreeItem> {
    return this.getScene().quadtree?.filter((item: QuadtreeItem) => !!this.getScene().elements[item.id]?.selectable);
  }

  getTargetsQuadtree(params = { includeHazards: true }): Quadtree<QuadtreeItem> {
    const { includeHazards } = params;
    return this.getSelectableQuadtree()
      ?.filter((item: QuadtreeItem) => {
        const itemFaction = this.getScene().elements[item.id]?.getFaction();
        const isInTargetGroup = this.getFactionTargetGroups().includes(itemFaction);
        if (itemFaction === Faction.HAZARD)
          return includeHazards && isInTargetGroup;
        return isInTargetGroup;
      })
      ;
  }

  getTargetsInCollision(): Array<Element> {
    this.framesSinceLastCollision++;
    if (this.framesSinceLastCollision < Element.numberOfFramesToSkipBetweenEachCollision)
      return [];

    this.framesSinceLastCollision = 0;
    const { x, y, width, height } = this;
    const quadtree = this.getTargetsQuadtree();
    if (!quadtree)
      return [];
    return quadtree
      .colliding(
        {
          x: x - width / 2,
          y: y - height / 2,
          width,
          height,
        }, (el1, el2) => {
          /* Custom function in which we measure the distance between ships instead of usual AABB box collision */
          const dist = getDistanceBy2XY({
            x1: el1.x + el1.width! / 2,
            y1: el1.y + el1.height! / 2,
            x2: el2.x + el2.width! / 2,
            y2: el2.y + el2.height! / 2,
          });
          const distToConsiderCollision =
            Math.min(el1.width!, el1.height!) / 2
            + Math.min(el2.width!, el2.height!) / 2;
          return (dist < distToConsiderCollision);
        }
      )
      .map((e: QuadtreeItem) => this.getScene().elements[e.id])
      ;
  }

  getQuadtreeElementsInRange(quadtree: Quadtree<QuadtreeItem>, range: number): Array<Element> {
    if (!quadtree)
      return [];
    return quadtree
      .colliding({
        x: this.x - range,
        y: this.y - range,
        width: range * 2,
        height: range * 2,
      })
      .map((e: QuadtreeItem) => this.getScene().elements[e.id])
      ;
  }

  getElementsInRange(quadtree = this.getSelectableQuadtree(), range: number): Array<Element> {
    return this.getQuadtreeElementsInRange(quadtree, range);
  }

  getNearestElement(elements: Element[]): Element | undefined {
    if (!elements.length)
      return;
    return this.sortElementsByDistance(elements)[0] as Element;
  }

  sortElementsByDistance(elementsArray: Array<Element>, order: 'asc' | 'desc' = 'asc'): Array<Element> {
    if (elementsArray.length === 0)
      return [];
    return elementsArray
      .sort((a: Element, b: Element) => {
        const distanceA = getDistanceBy2XY({
          x1: this.x,
          y1: this.y,
          x2: a.x,
          y2: a.y
        });
        const distanceB = getDistanceBy2XY({
          x1: this.x,
          y1: this.y,
          x2: b.x,
          y2: b.y
        });

        if (order === 'desc')
          return distanceB - distanceA;
        else
          return distanceA - distanceB;
      });
  }
}
